import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultTranslations = {
  en: {
    nav: {
      philosophy: 'Philosophy',
      experience: 'Experience',
      gallery: 'Gallery',
      membership: 'Membership',
    },
    hero: {
      tagline: 'Ritual. Discipline. Transformation.',
      subtitle: 'Munich\'s Premier Cold Exposure & Sauna Sanctuary',
      cta: 'Join Early Access',
      scroll: 'Enter',
    },
    philosophy: {
      quote: '"What stands in the way becomes the way."',
      author: '— Marcus Aurelius',
      title: 'The Path of Contrast',
      text1: 'In the ancient traditions of thermal bathing, our ancestors discovered a profound truth: transformation comes not from comfort, but from the deliberate embrace of extremes.',
      text2: 'Heat purifies. Cold awakens. Stillness integrates. This is the cycle of regeneration that has forged warriors, philosophers, and seekers for millennia. A practice rooted in longevity science and biohacking principles.',
      text3: 'At ICEBACH, we have created a sanctuary where this primal wisdom meets modern precision. Here, in the heart of Bavaria, you will discover what lies beyond your perceived limits.',
    },
    heat: {
      number: 'I',
      title: 'The Heat',
      subtitle: 'Purification Through Fire',
      description: 'Enter the inferno. At 90°C, the traditional Aufguss ceremony transforms the sauna into a crucible of transformation. As steam rises from volcanic stones, surrender to the heat that has cleansed humanity since the dawn of civilization.',
      temp: '90°C',
    },
    cold: {
      number: 'II',
      title: 'The Cold',
      subtitle: 'Awakening Through Ice',
      description: 'Descend into the crystalline embrace of our alpine-fed plunge pools. At 3°C, the shock of immersion strips away all that is unnecessary, leaving only presence, power, and an electrifying clarity that must be experienced to be believed.',
      temp: '3°C',
    },
    rest: {
      number: 'III',
      title: 'The Rest',
      subtitle: 'Integration in Nature',
      description: 'Between cycles, retreat to our forest sanctuary. Here, among ancient pines and drifting mist, the body integrates its transformation. Breathe. Ground. Return to your primal state of equilibrium.',
      element: 'Air',
    },
    gallery: {
      title: 'The Sanctuary',
      subtitle: 'A glimpse into the ritual',
    },
    membership: {
      title: 'Founding Circle',
      subtitle: 'Join the Inner Sanctum',
      description: 'Be among the first to experience ICEBACH. Founding members receive lifetime priority access, exclusive ceremonies, and rates that will never be offered again.',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        phone: 'Phone (Optional)',
        submit: 'Request Access',
        submitting: 'Submitting...',
      },
      spots: 'Limited to 100 founding members',
    },
    footer: {
      location: 'Munich, Bavaria',
      rights: 'All rights reserved.',
    },
  },
  de: {
    nav: {
      philosophy: 'Philosophie',
      experience: 'Erlebnis',
      gallery: 'Galerie',
      membership: 'Mitgliedschaft',
    },
    hero: {
      tagline: 'Ritual. Disziplin. Transformation.',
      subtitle: 'Münchens exklusivstes Kälte- & Sauna-Refugium',
      cta: 'Früher Zugang',
      scroll: 'Eintreten',
    },
    philosophy: {
      quote: '"Was im Weg steht, wird zum Weg."',
      author: '— Marcus Aurelius',
      title: 'Der Pfad des Kontrasts',
      text1: 'In den alten Traditionen des thermischen Badens entdeckten unsere Vorfahren eine tiefe Wahrheit: Transformation entsteht nicht aus Komfort, sondern aus der bewussten Umarmung der Extreme.',
      text2: 'Hitze reinigt. Kälte erweckt. Stille integriert. Dies ist der Zyklus der Regeneration, der Krieger, Philosophen und Suchende seit Jahrtausenden geformt hat. Eine Praxis, verwurzelt in Longevity-Wissenschaft und Biohacking-Prinzipien.',
      text3: 'Bei ICEBACH haben wir ein Refugium geschaffen, wo diese ursprüngliche Weisheit auf moderne Präzision trifft. Hier, im Herzen Bayerns, werden Sie entdecken, was jenseits Ihrer wahrgenommenen Grenzen liegt.',
    },
    heat: {
      number: 'I',
      title: 'Die Hitze',
      subtitle: 'Reinigung durch Feuer',
      description: 'Betreten Sie das Inferno. Bei 90°C verwandelt die traditionelle Aufguss-Zeremonie die Sauna in einen Schmelztiegel der Transformation. Während Dampf von vulkanischen Steinen aufsteigt, ergeben Sie sich der Hitze, die die Menschheit seit Anbeginn der Zivilisation gereinigt hat.',
      temp: '90°C',
    },
    cold: {
      number: 'II',
      title: 'Die Kälte',
      subtitle: 'Erwachen durch Eis',
      description: 'Tauchen Sie ein in die kristallklare Umarmung unserer alpengespeisten Tauchbecken. Bei 3°C streift der Schock der Immersion alles Unnötige ab und hinterlässt nur Präsenz, Kraft und eine elektrisierende Klarheit, die erlebt werden muss, um geglaubt zu werden.',
      temp: '3°C',
    },
    rest: {
      number: 'III',
      title: 'Die Ruhe',
      subtitle: 'Integration in der Natur',
      description: 'Zwischen den Zyklen ziehen Sie sich in unser Waldrefugium zurück. Hier, zwischen uralten Kiefern und schwebendem Nebel, integriert der Körper seine Transformation. Atmen. Erden. Kehren Sie zu Ihrem ursprünglichen Gleichgewichtszustand zurück.',
      element: 'Luft',
    },
    gallery: {
      title: 'Das Refugium',
      subtitle: 'Ein Einblick in das Ritual',
    },
    membership: {
      title: 'Gründerkreis',
      subtitle: 'Werde Teil des inneren Heiligtums',
      description: 'Gehören Sie zu den Ersten, die ICEBACH erleben. Gründungsmitglieder erhalten lebenslangen bevorzugten Zugang, exklusive Zeremonien und Konditionen, die nie wieder angeboten werden.',
      form: {
        name: 'Ihr Name',
        email: 'E-Mail-Adresse',
        phone: 'Telefon (Optional)',
        submit: 'Zugang anfragen',
        submitting: 'Wird gesendet...',
      },
      spots: 'Limitiert auf 100 Gründungsmitglieder',
    },
    footer: {
      location: 'München, Bayern',
      rights: 'Alle Rechte vorbehalten.',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [customContent, setCustomContent] = useState({});
  
  // Fetch custom content from backend
  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      const contentMap = {};
      response.data.forEach(item => {
        contentMap[item.section] = item;
      });
      setCustomContent(contentMap);
    } catch (error) {
      console.log('Using default content');
    }
  };
  
  useEffect(() => {
    fetchContent();
    // Refresh content every 30 seconds for admin updates
    const interval = setInterval(fetchContent, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const t = (key) => {
    const keys = key.split('.');
    let value = defaultTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };
  
  const getCustomContent = (section, field) => {
    const langField = `${field}_${language}`;
    return customContent[section]?.[langField] || null;
  };
  
  const getCustomMedia = (section, type = 'video') => {
    const field = type === 'video' ? 'video_url' : 'image_url';
    return customContent[section]?.[field] || null;
  };
  
  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      getCustomContent,
      getCustomMedia,
      customContent,
      setCustomContent,
      refreshContent: fetchContent
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: 'en',
      setLanguage: () => {},
      t: (key) => key,
      getCustomContent: () => null,
      getCustomMedia: () => null,
      customContent: {},
      setCustomContent: () => {},
      refreshContent: () => {}
    };
  }
  return context;
};
